import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import styles from './AllTagsInfo.module.scss';

import * as extraInfoDb from '../../../shared/db/extra-info-db';
import * as tagsDb from '../../../shared/db/tags-db';

import { Set, is } from 'immutable';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ACL, UserCan } from '../../../shared/constants';
import { ALL_DEVICES_TAG, compareStringsAlphabetically, idValArr } from '../../../shared/helpers';
import {
  UserAuth,
  iFullStoreState,
  iList,
  iTagInfoBase,
} from '../../../shared/interfaces';
import { hideAllTags } from '../../../stores/reducers/tagsDevicesMap/AC';

import { faPlus } from '@fortawesome/fontawesome-free-solid';
import classNames from 'classnames';
import { equals } from 'ramda';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { updateFilter } from '../../../shared/db/filters-db';
import { keyGen } from '../../../shared/firebase';
import DashboardBlock from '../../DashboardBlock';
import NewTagOrLabelModal, { NewTagFormValues } from './NewTagOrLabelModal';
import TagsListItem from './TagsListItem';
import { Fa } from '../../elements/fa';
import { useTagMap } from './useTagMap';
import { useTagsFilter } from './useTagsFilter';

interface iProps<TExtra = unknown> {
    authUser: UserAuth;
    userCanDo: string[];
    items: iList<iTagInfoBase<TExtra>>;
    isTagType: boolean;
}

const AllTagsInfoPage = (props: iProps) => <AllTagsInfo {...props} />

const AllTagsInfo = ({ items, isTagType, userCanDo }: iProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const [createTagOrLabel, setCreateTagOrLabel] = useState(false);
    const [filter, setFilter] = useState(false);

    const authUser = useSelector<iFullStoreState, UserAuth>((s) => s.auth.user, (l, r) => equals(l, r));
    const filters = useSelector<iFullStoreState, object>((state) => state.general.filters);

    const { toggleTag, devicesIds } = useTagMap(filter);

    const showedTagsIds = useSelector<iFullStoreState, Set<string>>(
        (state) => state.tagsDevicesMap.showedTagsIds,
        (left, right) => is(left, right),
    );

    const {
      searchQueryValue,
      setSearchQueryValue,
      tagsActualState,
    } = useTagsFilter({ items, authUser, userCanDo, devicesIds });

    const tagIdToView = new URLSearchParams(location.search).get('viewTag');

    const removeAllTags = () => {
        dispatch(hideAllTags());
    };

    useEffect(() => removeAllTags(), []);

    useEffect(() => {
      if (!isTagType || !tagIdToView) return;
      if (!tagsActualState[tagIdToView]) return;

      if (!showedTagsIds.has(tagIdToView)) {
        toggleTag(tagIdToView);
      }

      history.replace('/tags');
    }, [isTagType, tagIdToView, tagsActualState, showedTagsIds, toggleTag, history]);

    useEffect(() => {
      if (filters) {
        Object.keys(filters).forEach((filterId) => {
          updateFilter(authUser)(filterId, {
            ...filters[filterId],
            selected: false,
          });
        });
      }
    }, [showedTagsIds]);

    const isSuperAdmin = authUser.beToken['do-anything'];

    const tagsToShow = idValArr(tagsActualState as iList<iTagInfoBase<any>>)
      .filter((tag) => {
        const userHasRule = tag.val.details.isHidden
          ? isSuperAdmin || tag.val.details.author === authUser.uid
          : true;
        return userHasRule;
      })
      .sort((a, b) => {
        if (a.id === ALL_DEVICES_TAG) return -1;
        if (b.id === ALL_DEVICES_TAG) return 1;

        const nameA = a.val.details.name;
        const nameB = b.val.details.name;

        return compareStringsAlphabetically(nameA, nameB);
      });

    const deleteItem = (id: string) => {
        if (showedTagsIds.has(id)) toggleTag(id);

        isTagType ?
          tagsDb.deleteTags(authUser)(tagsActualState[id]) :
          extraInfoDb.deleteLabels(authUser)([id]);
    };

    const requestToCreateNewTagOrLabel = ({ tagName, isLabelHidden = false, isYesNoType, kind = 'select' }: any) => {
        return (
          (isTagType ? tagsDb : extraInfoDb)
            .update(authUser)
            (keyGen(), {
                name: tagName,
                icon: { svgIconName: 'micro', type: 1 },
                color: '#B80000',
                isHidden: isLabelHidden,
                owner: authUser.uid,
                yesNoSelection: isYesNoType,
                kind: kind
            })
        );
    }

    return (
      <DashboardBlock>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div className={styles.tagsList}>
            <PerfectScrollbar>
              {/* Add header */}
              <div
                className={classNames(styles.tableHeader, {
                  [styles.tableHeader_withThreeColumns]: isTagType,
                })}
              >
                <p className={styles.headerItem}>
                  {!showedTagsIds.size && ACL.check(UserCan.CREATE_TAGS, userCanDo) && (
								<button
									className={styles.addButton}
									onClick={() => setCreateTagOrLabel(true)}
									title="Add new tag"
								>
									<Fa icon={faPlus} />
								</button>
							)}

                  {isTagType ? 'Tag' : 'Label'}
                </p>

                {isTagType && (
                  <>
                    <p className={classNames(styles.headerItem, styles.centerHeaderItem)}>Vehicles</p>
                    <p className={classNames(styles.headerItem, styles.toRightHeaderItem)}>Actions</p>
                  </>
                )}
              </div>

              {tagsToShow.map(({ id, val: tag }, index) => (
                <TagsListItem
                  key={id}
                  details={tag.details}
                  id={id}
                  isViewed={!!showedTagsIds.size && showedTagsIds.has(id)}
                  isTagType={isTagType}
                  deleteItem={!!showedTagsIds.size ? null : deleteItem}
                  toggleTag={toggleTag}
                  index={index}
                />
              ))}
            </PerfectScrollbar>
          </div>
        </div>

        {createTagOrLabel && (
          <NewTagOrLabelModal
            onClose={() => setCreateTagOrLabel(false)}
            handleSubmit={requestToCreateNewTagOrLabel}
            isTagType={isTagType}
          />
        )}
      </DashboardBlock>
    );
};

const mapStateToProps = (state: iFullStoreState, ownProps) => {
    const authUser = state.auth.user;
    const userCanDo = state.auth?.user?.acl?.can ?? [];
    let items = {};
    let isTagType: boolean;

    if (ownProps.match.path.indexOf('/tags') === 0) {
        isTagType = true;
        items = (state.general.tags || {});
    }

    if (ownProps.match.path.indexOf('/extra-info') === 0) {
        isTagType = false;
        items = (state.general.extraInfo || {});
    }

    return {
        authUser,
        items,
        isTagType,
        userCanDo,
    };
};

export default connect(mapStateToProps)(AllTagsInfoPage);
